import { Availability, defaultAvail } from "@caredna-marlin/utils/client/availability";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Collapse, IconButton, ListSubheader, Menu, MenuItem, Skeleton, TableCell, TableRow, Tooltip, useTheme } from "@mui/material";

import { Dictionary, startCase } from "lodash";
import { useNavigate } from "react-router-dom";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { AvailabilityByTimespanQuery } from "~/graphql/__generated__/graphql";
import { Permission } from "~/hooks/permissions";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import AvailabilityTable from "./AvailabilityTable";
import { AvailabilityAction } from "~/hooks/useAvailabilityOperation";
import { startOfToday } from "date-fns";
import AddAvailabilityMenu from "./AddAvailabilityMenu";
import { useState } from "react";
import ProviderAsTemplateDialog from "./ProviderAsTemplateDialog";

type Props = {
  availability: Availability[],
  provider: AvailabilityByTimespanQuery["provider"][number],
  teams: Dictionary<AvailabilityByTimespanQuery["team"][number]>,
  setEditing: (editing: { action: AvailabilityAction, avail: Availability }) => void,
  expanded: boolean,
  setExpanded: (e: boolean) => void,
}

const ProviderTableRow = ({ availability, expanded, provider, setEditing, setExpanded, teams }: Props) => {
  const { providerTypes, serviceLines } = useOrgConfig();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const theme = useTheme();

  const teamNames = provider.teamProviders.map(tp => teams[tp.teamId]?.name).filter(Boolean);
  const serviceLineNames = provider.serviceLineProviders.map(slp => serviceLines[slp.serviceLineId]?.name).filter(Boolean);

  return <>
    <TableRow sx={{ "td": { borderBottom: "none" }}}>
      <TableCell sx={{ cursor: "pointer", paddingLeft: "0px" }} onClick={() => setExpanded(!expanded)}>
        <IconButton aria-label="Expand row" size="small" disableRipple sx={{ pl: 0 }}>
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <span style={{ color: theme.palette.primary.main, fontWeight: 600 }}>
          {provider.firstName} {provider.lastName}
        </span>
      </TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{startCase(providerTypes[provider.providerTypeId]?.name)}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{provider.addressStreet}, {provider.addressCity}, {provider.addressState}</TableCell>
      <TableCell sx={{ maxWidth: 220, whiteSpace: "normal", wordBreak: "break-word", fontSize: "0.85rem", fontWeight: 500 }}>
        {serviceLineNames.join(", ")}
      </TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{teamNames.join(", ")}</TableCell>
      <TableCell sx={{ width: "3em", textAlign: "right", pr: 1 }}>
        <ProviderActionsMenu
          hasAvailability={availability.length > 0}
          provider={provider}
          setEditing={setEditing}
          setTemplateDialogOpen={setTemplateDialogOpen}
        />
      </TableCell>
    </TableRow>
    <TableRow sx={{ "td": { borderBottom: `1px solid ${theme.palette.divider}` }}}>
      <TableCell colSpan={6} sx={{ p: 0 }}>
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          sx={{pl: 0, pr: 0}}
          aria-description={`Schedule for ${provider.firstName} ${provider.lastName}`}
        >
          <AvailabilityTable availability={availability} setEditing={setEditing} providerId={provider.id} />
        </Collapse>
      </TableCell>
    </TableRow>

    {templateDialogOpen && <ProviderAsTemplateDialog
      availability={availability}
      onClose={() => setTemplateDialogOpen(false)}
      provider={provider}
    />}
  </>
}

export default ProviderTableRow;

export const ProviderTableRowSkeleton = () => (
  <TableRow>
    <TableCell sx={{ width: "20em" }}><Skeleton width="100%" /></TableCell>
    <TableCell sx={{ width: "1em" }}><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
    <TableCell><Skeleton width="100%" /></TableCell>
  </TableRow>
);

type MenuProps = {
  provider: Props["provider"],
  setEditing: Props["setEditing"],
  setTemplateDialogOpen: (open: boolean) => void,
  hasAvailability: boolean,
};
const ProviderActionsMenu = ({ hasAvailability, provider, setEditing, setTemplateDialogOpen }: MenuProps) => {
  const navigate = useNavigate();
  const [actionsOpen, setActionsOpen] = useState<null | HTMLElement>(null);

  return <>
    <IconButton aria-label="Actions" onClick={(e) => setActionsOpen(e.currentTarget)}>
      <MoreHorizIcon />
    </IconButton>
    <Menu open={!!actionsOpen} onClose={() => setActionsOpen(null)} anchorEl={actionsOpen}>
      <PermissionOrHide permission={Permission.manageUsers}>
        <MenuItem onClick={() => navigate(`/users/manage/${provider.organizationUserId}`)}>
          Manage Provider
        </MenuItem>
      </PermissionOrHide>
      {hasAvailability && <Tooltip
        title="Use part of this provider's schedule as a template, overwriting other providers' schedule"
        arrow
        placement="right"
      >
        <MenuItem onClick={() => { setTemplateDialogOpen(true); setActionsOpen(null); }}>
          Use as Template
        </MenuItem>
      </Tooltip>}
      <ListSubheader sx={{ minWidth: 170, cursor: "default" }}>Add to calendar</ListSubheader>
      <AddAvailabilityMenu sx={{ ml: 2 }} handleMenuClick={(type, blockType) => {
        setEditing({ action: "create", avail: defaultAvail(type, startOfToday(), provider.id, blockType) });
        setActionsOpen(null);
      }} />
    </Menu>
  </>
};
